import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { Search, ChevronRight, Mail, Wifi, RefreshCw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { StatusBadge } from "@/components/StatusBadge";
import { format } from "@/lib/brand-tokens";
import { applications, getAdminById } from "@/lib/mock-data";
import { listLiveApplications } from "@/lib/onboarding-api";

// ADM-05 — Applications review queue
const TABS = [
  { id: "PendingReview",         label: "Pending review" },
  { id: "KYCInProgress",         label: "KYC in progress" },
  { id: "ApprovedAwaitingKYC",   label: "Awaiting KYC" },
  { id: "all",                   label: "All" },
];

// Normalize a backend application to the same flat shape the seeded ones use
// (`a.city`, `a.state`, `a.appliedAt`, `a.kycStatus`…).
function normalizeLiveApp(a) {
  return {
    ...a,
    isLive:        true,
    appliedAt:     a.submittedAt || a.appliedAt,
    city:          a.address?.city,
    state:         a.address?.state,
    kycStatus:     a.kycStatus || "NotStarted",
  };
}

export default function ApplicationsQueuePage() {
  const [tab, setTab] = useState("PendingReview");
  const [q, setQ] = useState("");
  const [liveApps, setLiveApps] = useState([]);
  const [liveError, setLiveError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const loadLive = async () => {
    setRefreshing(true);
    try {
      const list = await listLiveApplications();
      setLiveApps(list.map(normalizeLiveApp));
      setLiveError(null);
    } catch (e) {
      setLiveError(e.message);
    } finally {
      setRefreshing(false);
    }
  };
  useEffect(() => {
    loadLive();
    // Auto-refresh every 15s so newly approved/KYC'd apps show up live during a demo.
    const id = setInterval(loadLive, 15000);
    return () => clearInterval(id);
  }, []);

  // Live apps always render BEFORE the seeded ones so demo activity is obvious.
  const combined = useMemo(() => [...liveApps, ...applications], [liveApps]);

  const filtered = useMemo(() => {
    const sorted = [...combined].sort((a, b) => new Date(a.appliedAt) - new Date(b.appliedAt));
    return sorted.filter((a) => {
      if (tab !== "all" && a.status !== tab) return false;
      if (q && !`${a.firstName} ${a.lastName} ${a.email}`.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [combined, tab, q]);

  return (
    <div data-testid="applications-queue-page" className="space-y-5 pb-10">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="eyebrow">Onboarding</div>
          <h1 className="font-brand text-display text-ink leading-tight mt-1">Applications</h1>
          <p className="text-body-sm text-ink-muted mt-1">
            FIFO queue · oldest first · {filtered.length} of {combined.length} matching
            {liveApps.length > 0 && (
              <span className="ml-2 inline-flex items-center gap-1 text-emerald-700 font-semibold">
                <Wifi className="size-3" /> {liveApps.length} live
              </span>
            )}
          </p>
        </div>
        <button onClick={loadLive} data-testid="refresh-live-apps"
                className={`inline-flex items-center gap-1.5 text-caption font-semibold text-ink-muted hover:text-ink ${refreshing ? "opacity-60" : ""}`}>
          <RefreshCw className={`size-3.5 ${refreshing ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {liveError && (
        <div className="text-caption text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
          Couldn't reach live application backend: {liveError}. Showing seeded data only.
        </div>
      )}

      <div className="-mx-8 bg-surface border-y border-line">
        <div className="px-8 py-4 border-b border-line flex flex-wrap items-center gap-3">
          <div className="flex bg-surface-alt rounded-md p-0.5">
            {TABS.map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`apps-tab-${t.id}`}
                      className={`px-3 py-1.5 rounded-md text-caption font-semibold transition-all ${
                        tab === t.id ? "bg-surface text-ink shadow-xs" : "text-ink-muted hover:text-ink"
                      }`}>
                {t.label}{t.id !== "all" && (
                  <span className="ml-1.5 text-ink-hint tabular-nums">({combined.filter((a) => a.status === t.id).length})</span>
                )}
              </button>
            ))}
          </div>
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-ink-hint" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email…" className="pl-8 h-9 bg-surface-alt border-transparent focus-visible:bg-surface focus-visible:border-line" />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-body-sm">
            <thead className="bg-surface-alt">
              <tr className="text-left">
                <th className="px-8 py-3 text-eyebrow uppercase text-ink-hint">Applicant</th>
                <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint">Applied</th>
                <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint">Status</th>
                <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint">KYC</th>
                <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint">Source-of-cards</th>
                <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint">Reviewer</th>
                <th className="px-8 py-3 text-eyebrow uppercase text-ink-hint text-right"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line/60">
              {filtered.map((a) => {
                const reviewer = a.reviewerId ? getAdminById(a.reviewerId) : null;
                const initials = `${(a.firstName?.[0] || "?")}${(a.lastName?.[0] || "?")}`;
                return (
                  <tr key={a.id} className={`hover:bg-surface-alt/40 ${a.isLive ? "bg-emerald-50/40" : ""}`}>
                    <td className="px-8 py-3.5">
                      <Link to={a.isLive ? `/admin/applications/live/${a.id}` : `/admin/applications/${a.id}`} className="flex items-center gap-3 group">
                        <div className={`size-8 rounded-full grid place-items-center text-caption font-semibold ${a.isLive ? "bg-emerald-100 text-emerald-700" : "bg-brand-50 text-brand-700"}`}>{initials}</div>
                        <div className="min-w-0">
                          <div className="text-ink font-semibold group-hover:text-brand-700 transition-colors flex items-center gap-2">
                            {a.firstName} {a.lastName}
                            {a.isLive && <span className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700 bg-surface border border-emerald-200 px-1.5 py-px rounded">Live</span>}
                          </div>
                          <div className="text-caption text-ink-subtle truncate">{a.email} · {a.city}, {a.state}</div>
                        </div>
                      </Link>
                    </td>
                    <td className="px-3 py-3.5 text-ink-muted text-caption tabular-nums whitespace-nowrap">
                      <div className="text-ink">{format.date(a.appliedAt)}</div>
                      <div className="text-ink-subtle">{format.relative(a.appliedAt)}</div>
                    </td>
                    <td className="px-3 py-3.5"><StatusBadge entity="application" status={a.status} dot /></td>
                    <td className="px-3 py-3.5">
                      <KycSubBadge status={a.kycStatus} />
                    </td>
                    <td className="px-3 py-3.5 text-ink-muted text-caption max-w-[280px] truncate" title={a.sourceOfCards}>{a.sourceOfCards}</td>
                    <td className="px-3 py-3.5 text-caption">
                      {reviewer ? <span className="text-ink-muted">{reviewer.firstName} {reviewer.lastName[0]}.</span>
                                : <span className="text-ink-hint">Unassigned</span>}
                    </td>
                    <td className="px-8 py-3.5 text-right">
                      <Link to={a.isLive ? `/admin/applications/live/${a.id}` : `/admin/applications/${a.id}`}
                            className="inline-flex items-center gap-1 text-caption font-semibold text-brand-700 hover:text-brand-800">
                        Review <ChevronRight className="size-3.5" />
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function KycSubBadge({ status }) {
  const map = {
    NotStarted: { label: "Not started", tone: "bg-slate-100 text-slate-700" },
    InProgress: { label: "In progress", tone: "bg-sky-50 text-sky-700" },
    Passed:     { label: "Passed",       tone: "bg-emerald-50 text-emerald-700" },
    Failed:     { label: "Failed",       tone: "bg-red-50 text-red-700" },
  };
  const s = map[status] || map.NotStarted;
  return <span className={`text-caption px-2 py-0.5 rounded-full font-medium ${s.tone}`}>{s.label}</span>;
}
